import React from "react"
import { ColumnDef } from "@tanstack/react-table"

// needed for table body level scope DnD setup
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable"

// needed for row & cell level scope DnD setup
import { GenericResource } from "models/generic"
import { twMerge } from "tailwind-merge"
import { DragDropVerticalIcon } from "components-old/icons"
import { VirtualInfiniteTableProps } from "../virtual/VirtualInfiniteTable"
import TableHead from "../TableHead"
import { useTable } from "../useTable"
import { DraggableRow } from "./DraggableRow"
import { useAtom } from "jotai"
import { isMobileAtom } from "atoms/app.atom"
import { isAldeia } from "hooks/useAldeia"
import useStuck from "hooks/useStuck"
import VirtualTableStaticSkeleton from "../virtual/VirtualTableStaticSkeleton"
import useFixScrollOffset from "hooks/useFixScrollOffset"

export interface DnDInfiniteTableProps<T extends GenericResource> extends VirtualInfiniteTableProps<T> {}

// Table Component
export function DnDInfiniteTable<T extends GenericResource>(props: DnDInfiniteTableProps<T>) {
  const [isMobile] = useAtom(isMobileAtom)

  //we need a reference to the scrolling element for logic down below
  const tableContainerRef = React.useRef<HTMLDivElement>(null)
  const innerRef = React.useRef<HTMLDivElement>(null)

  useFixScrollOffset({
    containerRef: tableContainerRef as any,
    innerRef: innerRef as any,
    enabled: isAldeia && props.layout === "static",
    refresher: props.data?.length,
  })
  const { dummy } = useStuck({ enabled: isAldeia && props.layout === "static", scrollContainer: tableContainerRef.current! })

  const columns = React.useMemo<ColumnDef<T>[]>(
    () => [
      // Create a dedicated drag handle column. Alternatively, you could just set up dnd events on the rows themselves.
      ...(!isMobile
        ? [
            {
              id: "drag-handle",
              cell: ({ row }) => (
                <DragDropVerticalIcon className="w-4 h-4 transition-transform duration-[0.2s] ease-[cubic-bezier(0.02,1.505,0.745,1.235)] group-hover/row:scale-125" />
              ),
              size: 40,
            },
          ]
        : []),
      ...props.columns,
    ],
    [isMobile, props.columns],
  )

  const { rows, fetchMoreOnBottomReached, table, bodyRowClassname, bodyRowCellClassname } = useTable<T>({
    ...props,
    columns,
    reactTableOptions: {
      getRowId: (row) => {
        return row.id
      },
    },
    tableContainerRef: tableContainerRef as any,
    scrollToTop: () => {
      // rowVirtualizer.scrollToIndex?.(0)
    },
  })

  const dataIds = React.useMemo<UniqueIdentifier[]>(() => props.data?.map(({ id }) => id), [props.data])

  // reorder rows after drag & drop
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event

    if (active.id != over?.id) {
      const oldIndex = props.data.findIndex((x) => x.id == active.id)
      const newIndex = props.data.findIndex((x) => x.id == over?.id)
      const newArray = arrayMove(props.data, oldIndex, newIndex)
      props.onSaveNewPositions?.(newArray)
    }
  }

  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}))

  const loading = (props?.isFetching && !props?.data?.length) || props.isFetchingNextPage

  return (
    <div
      className={twMerge("max-w-full overflow-x-auto aldeia:pb-20", props.className)}
      // onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
      ref={tableContainerRef}
      style={{
        position: "relative", //needed for sticky header
        ...(isAldeia
          ? {
              height: "calc(var(--app-container-height) - var(--static-header-height))",
            }
          : {}),
        // ...(isSafari && !props.local ? { height: "calc(100vh - 60px)" } : {}),
      }}
    >
      <div ref={innerRef} className="w-full h-full">
        {dummy}
        {props.children}
        <DndContext collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd} sensors={sensors}>
          <div className="g_table_wrapper">
            <table className="w-full">
              <TableHead table={table} transparentHeader={props.transparentHeader} />
              <tbody
                style={{
                  position: "relative", //needed for absolute positioning of rows
                }}
              >
                <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
                  {rows.map((row) => (
                    <DraggableRow
                      key={row.id}
                      id={row.id}
                      row={row}
                      onClickRow={props.onClickRow}
                      bodyRowClassname={bodyRowClassname}
                      bodyRowCellClassname={bodyRowCellClassname}
                    />
                  ))}
                </SortableContext>
              </tbody>
            </table>
            {loading && isAldeia && <VirtualTableStaticSkeleton rowHeight={props.rowHeight} />}
          </div>
        </DndContext>
      </div>
    </div>
  )
}
